import cx from "classnames";
import { Fragment } from "react";

import { useTranslation } from "@/core/i18n";
import { DescriptiveTag } from "atoms/tag/DescriptiveTag";
import { ModalSelector } from "molecules/containers/modal-selector-list/entry/ModalSelector";
import { ListModalSize } from "molecules/modal-window/list-modal/ListModal";
import { ListModal } from "molecules/modal-window/list-modal/ListModal";
import { formatCurrency } from "toolbox/Money";

type BaseSelectModalProps = {
  open?: boolean;
  /** Omit to require confirmation without a close button or outside-click dismissal. */
  onClose?: () => void;
  title: string;
  subtitle?: React.ReactNode;
  size?: ListModalSize;
  type: "sort" | "filter" | "products" | "location" | undefined;
  selectionType?: "single" | "multiple" | undefined;
  items: {
    id: string;
    title: string;
    priceOrResults?: number | undefined;
    membershipPrice?: number | null;
    description?: string | null;
    subtitle?: string;
    /**
     * Replaces the price/results line this component would compose from `priceOrResults`, for a
     * caller that has to say something about the price the shared formatting cannot ("$82 each").
     * Nothing is rendered when the caller has neither.
     */
    priceLabel?: string;
    /** What the option is worth under the offer being chosen, e.g. "3 at 50% off · Save $123". */
    valueLine?: string;
    /** Heading rendered as a divider immediately above this row, e.g. a group of unavailable items. */
    groupLabel?: string;
    disabled?: boolean;
    tagText?: string | null;
    tagColor?: string | null;
    showWarning?: boolean;
  }[];
  checkedItems: string[] | string;
  onSelect: (value: string) => void;
  onFirstButtonClick: () => void;
  firstButtonLabel: React.ReactNode;
  firstButtonDisabled?: boolean;
  onSecondButtonClick?: undefined;
  secondButtonLabel?: undefined;
  secondButtonDisabled?: boolean;
  isVariantsModal?: boolean;
  errorMessage?: string;
  displayError?: boolean;
  hasInvalidItemLocation?: boolean;
  /** Overrides the modal header's default centred, uppercased treatment. */
  titleClassName?: string;
  /** Overrides the subtitle block's default centred treatment. */
  subtitleClassName?: string;
};

type WithSecondButtonProps = {
  onSecondButtonClick: () => void;
  secondButtonLabel: string;
} & Omit<BaseSelectModalProps, "secondButtonLabel" | "onSecondButtonClick">;

type SelectModalProps = BaseSelectModalProps | WithSecondButtonProps;

export const ModalSelectorList = ({
  open,
  onClose,
  title,
  subtitle,
  type,
  selectionType,
  size,
  items,
  checkedItems,
  onSelect,
  onFirstButtonClick,
  firstButtonLabel,
  firstButtonDisabled,
  onSecondButtonClick,
  secondButtonLabel,
  secondButtonDisabled,
  isVariantsModal = false,
  errorMessage,
  displayError = false,
  titleClassName,
  subtitleClassName,
}: SelectModalProps) => {
  const { t } = useTranslation();
  const setLabel = (
    membershipPrice: number | null | undefined,
    priceOrResults: number | undefined,
  ) => {
    const initialLabel = isVariantsModal ? t("modalSelectorList.from") : "";
    if (type === "products" && priceOrResults) {
      if (membershipPrice) {
        return (
          <>
            {initialLabel}
            <span className="text-body2">{formatCurrency(priceOrResults)} | </span>
            <span className="text-body2 text-primary">
              {formatCurrency(membershipPrice)} {t("modalSelectorList.member")}
            </span>
          </>
        );
      }
      return (
        <>
          {initialLabel}
          <span className="text-body2">{formatCurrency(priceOrResults)}</span>
        </>
      );
    } else if (type === "filter") {
      return (
        <>
          {initialLabel}
          <span className="text-body2">
            {priceOrResults ?? 0} {t("modalSelectorList.result", { count: priceOrResults ?? 0 })}
          </span>
        </>
      );
    }
  };

  const modalProps = {
    open,
    onClose,
    size,
    title,
    subtitle,
    firstButtonLabel,
    onFirstButtonClick,
    firstButtonDisabled,
    titleClassName:
      type === "location" ? "text-center whitespace-nowrap" : (titleClassName ?? undefined),
  };

  const content = (
    <>
      {subtitle && (
        <div className={cx("p-4 pb-3", subtitleClassName ?? "text-center")}>{subtitle}</div>
      )}
      <div className={cx("px-4", type === "location" && "max-h-[400px] overflow-y-auto")}>
        {items?.map((item) => {
          return (
            <Fragment key={item.id}>
              {item.groupLabel && (
                <div className="border-t border-background-two bg-one px-1 pb-2 pt-4 text-chip uppercase tracking-[.07em] text-secondary">
                  {item.groupLabel}
                </div>
              )}
              <ModalSelector
                tag={
                  item.tagText && (
                    <DescriptiveTag
                      color={item.tagColor ? "custom" : "primary"}
                      size="small"
                      customColor={item.tagColor}
                    >
                      {item.tagText}
                    </DescriptiveTag>
                  )
                }
                title={item.title || ""}
                checked={checkedItems?.includes(item.id)}
                description={item.description}
                onChange={() => {
                  onSelect(item.id);
                }}
                label={item.priceLabel ?? setLabel(item.membershipPrice, item.priceOrResults)}
                type={
                  type === "filter"
                    ? "checkbox"
                    : selectionType === "multiple"
                      ? "checkbox"
                      : "radio"
                }
                subtitle={item.subtitle}
                valueLine={item.valueLine}
                disabled={item.disabled}
                isLocationType={type === "location"}
                showWarning={item.showWarning}
              />
            </Fragment>
          );
        })}
      </div>
      {displayError && errorMessage && (
        <div className="mt-2 px-7 pb-4">
          <div className="text-sm font-semibold text-red">{errorMessage}</div>
        </div>
      )}
    </>
  );

  if (secondButtonLabel && onSecondButtonClick) {
    return (
      <ListModal
        {...modalProps}
        secondButtonLabel={secondButtonLabel}
        onSecondButtonClick={onSecondButtonClick}
        secondButtonDisabled={secondButtonDisabled}
      >
        {content}
      </ListModal>
    );
  }
  return <ListModal {...modalProps}>{content}</ListModal>;
};
